import { useMemo, useState } from 'react';
import { ClipboardPaste, CheckCircle2, RotateCcw, ArrowRight } from 'lucide-react';
import SelecteurAttendusCompact from './SelecteurAttendusCompact';
import ChoixAttendu from './ChoixAttendu';
import { colonnesNonImportees } from '../../utils/collerNotes';
import { periodeDeLaDate, libellePeriode } from '../../utils/periode';

/**
 * Étape 1 du wizard « Coller mes notes existantes ».
 *
 * Mode NOTE SEULE : collage + configuration + sélection d'attendus (cartes
 * avec compétences multiples par attendu).
 *
 * Mode DÉCOMPOSÉ : le collage contient des colonnes de maîtrise — le
 * MAPPAGE est l'objet central : une ligne par colonne collée, avec son
 * dropdown d'attendu (recherche, pré-mappage « proposition ») et sa
 * compétence. La flèche montre la règle, rien à lire. Un doublon exact
 * (même attendu · même compétence sur 2 lignes) est signalé sur les lignes
 * et bloque. Une colonne laissée SANS attendu n'est pas importée (2026-09-29,
 * demande d'une enseignante : sa colonne « Communiquer sa réponse » n'a pas
 * d'attendu) : la ligne le dit (« Non importée ») et c'est annoncé sous la
 * liste ; il faut au moins une colonne importée. Les compétences d'une ligne
 * n'apparaissent qu'une fois son attendu choisi.
 *
 * Mise en page (revue d'ergonomie, 2026-09-29) : titre, note et date sur une
 * ligne en haut, avec la période du suivi que la date désigne ; les colonnes
 * (ou les attendus) sur toute la largeur en dessous.
 */
const EtapeCollerConfigurer = ({
    parseResult, onColler, onRecoller,
    titre, setTitre, dateEval, setDateEval, bareme, setBareme,
    competences,
    themes, attendusSelectionnes, onToggleAttendu, onToggleCompAttendu,
    mode, mappages, onChangeMappage, attendusNiveau, doublons,
    echelleChiffres, onChangeEchelle,
    periodes = [],
    niveauClasse = '',
    onVerifier,
}) => {
    const [erreurClipboard, setErreurClipboard] = useState(null);
    // La date range les notes dans un trimestre : on montre lequel.
    const periodeSuivi = useMemo(() => periodeDeLaDate(dateEval, periodes), [dateEval, periodes]);

    const lireDepuisPressePapier = async () => {
        setErreurClipboard(null);
        if (!navigator.clipboard?.readText) {
            setErreurClipboard('Votre navigateur ne permet pas la lecture directe : collez dans la zone ci-dessous (Ctrl+V).');
            return;
        }
        try {
            const texte = await navigator.clipboard.readText();
            onColler(texte);
        } catch {
            setErreurClipboard('Lecture du presse-papier refusée : collez dans la zone ci-dessous (Ctrl+V).');
        }
    };

    const collageOk = parseResult && !parseResult.erreur;
    const nbNotes = collageOk ? parseResult.lignes.filter((l) => l.note !== null).length : 0;
    const colonnesMaitrise = parseResult?.colonnesMaitrise || [];
    const decompose = mode === 'decompose';

    const attenduParId = useMemo(
        () => new Map((attendusNiveau || []).map((a) => [a.id, a])),
        [attendusNiveau]
    );

    const nonImportees = decompose ? colonnesNonImportees(mappages, colonnesMaitrise) : [];
    const nbImportees = decompose ? (mappages || []).length - nonImportees.length : 0;

    // Colonnes en chiffres : l'échelle est une politique d'ÉTABLISSEMENT,
    // un seul réglage pour tout le fichier.
    const aColonnesChiffrees = colonnesMaitrise.some((c) => c.chiffres);
    const maxChiffre = colonnesMaitrise.reduce((m, c) => Math.max(m, c.chiffres ? (c.maxObserve || 0) : 0), 0);
    const surQuatreImpossible = maxChiffre > 4;

    const verifierDisabled = !collageOk
        || !titre.trim()
        || !dateEval
        || (!decompose && !(parseFloat(bareme) > 0))
        || (decompose
            ? (nbImportees === 0 || doublons.size > 0)
            : (attendusSelectionnes.length === 0
                || attendusSelectionnes.some((a) => (a.compIds || []).length === 0)));

    // ── Zone de collage (bannière ou invite) ──
    const zoneCollage = collageOk ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-emerald-300 bg-emerald-50">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <p className="text-sm font-bold text-emerald-800 flex-1">
                {parseResult.lignes.length} élève{parseResult.lignes.length > 1 ? 's' : ''} détecté{parseResult.lignes.length > 1 ? 's' : ''}
                {decompose
                    ? ` · ${colonnesMaitrise.length} compétence${colonnesMaitrise.length > 1 ? 's' : ''} décomposée${colonnesMaitrise.length > 1 ? 's' : ''}${nbNotes > 0 ? ' (la colonne Notes est ignorée)' : ''}`
                    : ` · ${nbNotes} note${nbNotes > 1 ? 's' : ''}`}
                {parseResult.ignorees.length > 0 ? ` · ${parseResult.ignorees.length} ligne(s) non lue(s)` : ''}
            </p>
            <button
                type="button"
                onClick={onRecoller}
                className="inline-flex items-center gap-1.5 text-sm font-extrabold text-emerald-700 hover:text-emerald-900"
            >
                <RotateCcw className="w-4 h-4" /> Recoller
            </button>
        </div>
    ) : (
        <div className="border-2 border-dashed border-blue-300 bg-blue-50/50 rounded-2xl p-6 text-center space-y-3">
            <p className="text-sm font-semibold text-slate-600">
                Dans votre logiciel de notes ou votre tableur : sélectionnez le tableau
                (les élèves et leurs notes), <strong>avec ses titres de colonnes</strong>,
                copiez-le, puis :
            </p>
            <button
                type="button"
                onClick={lireDepuisPressePapier}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-extrabold px-5 py-2.5 rounded-xl transition-colors"
            >
                <ClipboardPaste className="w-4 h-4" />
                Coller depuis le presse-papier
                <span className="bg-blue-500 rounded px-1.5 py-0.5 text-[11px] font-mono">Ctrl+V</span>
            </button>
            {(erreurClipboard || parseResult?.erreur) && (
                <p className="text-sm font-semibold text-amber-700">
                    {parseResult?.erreur || erreurClipboard}
                </p>
            )}
            <textarea
                rows={3}
                placeholder="…ou collez directement ici (Ctrl+V)"
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-blue-400 outline-none resize-y"
                onPaste={(e) => {
                    e.preventDefault();
                    onColler(e.clipboardData.getData('text/plain'));
                }}
                onChange={(e) => {
                    if (e.target.value.trim()) onColler(e.target.value);
                }}
                value=""
            />
        </div>
    );

    // ── Configuration (titre / barème / date) : une seule ligne en haut ──
    const blocConfiguration = (
        <div className={`grid grid-cols-1 gap-4 items-start ${decompose ? 'md:grid-cols-[minmax(0,1fr),15rem]' : 'md:grid-cols-[minmax(0,1fr),8rem,15rem]'}`}>
            <div>
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                    Titre de l'évaluation
                </label>
                <input
                    type="text"
                    value={titre}
                    onChange={(e) => setTitre(e.target.value)}
                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold focus:border-blue-400 outline-none"
                />
            </div>
            {!decompose && (
                <div>
                    <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                        Note sur
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={bareme}
                        onChange={(e) => setBareme(e.target.value)}
                        className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold focus:border-blue-400 outline-none"
                    />
                </div>
            )}
            <div>
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                    Date de l'évaluation
                </label>
                <input
                    type="date"
                    value={dateEval}
                    onChange={(e) => setDateEval(e.target.value)}
                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold focus:border-blue-400 outline-none"
                />
                {periodeSuivi ? (
                    <p className="text-[12px] font-bold text-blue-700 mt-1">
                        Période du suivi : {libellePeriode(periodeSuivi)}
                    </p>
                ) : periodes.length > 0 ? (
                    <p className="text-[12px] font-bold text-amber-700 mt-1">
                        Cette date n'est dans aucune période de l'établissement.
                    </p>
                ) : null}
            </div>
        </div>
    );

    // ── Mappage (décomposé) : une ligne par colonne collée ──
    const tableMappage = decompose && collageOk && (
        <div>
            {/* Échelle des compétences chiffrées : politique d'établissement,
                réglée UNE fois pour tout le fichier. */}
            {aColonnesChiffrees && (
                <div className="border-2 border-slate-100 bg-slate-50/60 rounded-xl px-4 py-3 mb-4">
                    <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                        Échelle des compétences de votre établissement
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onChangeEchelle('6')}
                            className={`px-3 py-1.5 rounded-full text-xs font-extrabold border-2 transition-colors ${
                                echelleChiffres === '6'
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
                            }`}
                        >
                            Sur 6 niveaux
                        </button>
                        <button
                            type="button"
                            disabled={surQuatreImpossible}
                            title={surQuatreImpossible ? `Impossible : le collage contient un ${maxChiffre}.` : undefined}
                            onClick={() => onChangeEchelle('4')}
                            className={`px-3 py-1.5 rounded-full text-xs font-extrabold border-2 transition-colors ${
                                echelleChiffres === '4'
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : surQuatreImpossible
                                        ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                                        : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
                            }`}
                        >
                            Sur 4 niveaux
                        </button>
                    </div>
                    <p className="text-[12px] font-semibold text-slate-500 mt-1.5">
                        {echelleChiffres === '4'
                            ? 'Sur 4 niveaux : 1 → Maîtrise insuffisante, 2 → Maîtrise fragile, 3 → Maîtrise satisfaisante, 4 → Très bonne maîtrise.'
                            : 'Sur 6 niveaux : de 1 (Maîtrise insuffisante) à 6 (Très bonne maîtrise), valeurs reprises telles quelles.'}
                    </p>
                </div>
            )}

            {/* En-têtes de mappage : la frontière source → destination, dite
                une fois pour toutes (masquée en mobile, où les lignes
                s'empilent). */}
            <div className="hidden md:grid md:grid-cols-[minmax(0,2fr),auto,minmax(0,3fr)] items-center gap-x-3 px-3 mb-1.5">
                <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wide">Dans votre fichier</span>
                <span className="w-4" />
                <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wide">Dans TanoEd (attendu · compétence)</span>
            </div>
            <label className="md:hidden block text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                Dans votre fichier → dans TanoEd
            </label>

            <div className="space-y-2">
                {(mappages || []).map((m) => {
                    const col = colonnesMaitrise[m.colIndex];
                    const enDoublon = doublons.has(m.colIndex);
                    return (
                        <div
                            key={m.colIndex}
                            className={`rounded-xl border-2 px-3 py-2.5 ${enDoublon ? 'border-red-300 bg-red-50/60' : 'border-slate-100 bg-white'}`}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr),auto,minmax(0,3fr)] items-center gap-x-3 gap-y-2">
                                <p className="text-[13px] font-bold text-slate-600 leading-snug">
                                    « {col?.entete} »
                                </p>
                                <ArrowRight className="w-4 h-4 text-slate-300 hidden md:block" />
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            <ChoixAttendu
                                                // La ligne garde l'attendu COMPLET qu'on y choisit (il peut venir
                                                // d'un autre niveau que la classe, 2026-09-29) ; une ligne proposée
                                                // n'a que l'id, retrouvé dans la liste du niveau de la classe.
                                                valeur={m.attenduId != null ? (m.attendu || attenduParId.get(m.attenduId) || null) : null}
                                                onChange={(a) => onChangeMappage(m.colIndex, { attenduId: a.id, attendu: a })}
                                                onVider={() => onChangeMappage(m.colIndex, { attenduId: null, attendu: null })}
                                                niveauClasse={niveauClasse}
                                                libelleVide="Non importée · choisir un attendu…"
                                                themes={themes}
                                                attendus={attendusNiveau}
                                            />
                                        </div>
                                        {m.propose && (
                                            <span
                                                className="flex-shrink-0 text-[11px] font-extrabold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5"
                                                title="Attendu proposé d'après le libellé de la colonne : vérifiez ou changez-le."
                                            >
                                                proposition
                                            </span>
                                        )}
                                    </div>
                                    {/* Les compétences n'apparaissent qu'avec un attendu : sur une
                                        ligne vide (non importée), un choix grisé laissait croire à
                                        une sélection (revue d'ergonomie, 2026-09-29). */}
                                    {m.attenduId != null ? (
                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                            {competences.map((c) => (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => onChangeMappage(m.colIndex, { compId: c.id })}
                                                    className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${
                                                        m.compId === c.id
                                                            ? 'bg-blue-600 border-blue-600 text-white'
                                                            : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300'
                                                    }`}
                                                >
                                                    {c.nom}
                                                </button>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            {enDoublon && (
                                <p className="text-[12px] font-bold text-red-600 mt-1.5">
                                    Même attendu et même compétence qu'une autre ligne : changez la compétence ou l'attendu.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            {doublons.size > 0 ? (
                <p className="text-sm font-extrabold text-amber-700 mt-2">
                    Deux lignes visent la même case : corrigez les lignes en rouge.
                </p>
            ) : nbImportees === 0 ? (
                <p className="text-sm font-extrabold text-amber-700 mt-2">
                    Choisissez l'attendu d'au moins une colonne.
                </p>
            ) : nonImportees.length > 0 ? (
                <p className="text-sm font-bold text-slate-600 mt-2">
                    {nonImportees.length > 1
                        ? `${nonImportees.length} colonnes ne seront pas importées : `
                        : '1 colonne ne sera pas importée : '}
                    {nonImportees.map((e) => `« ${e} »`).join(', ')}.
                </p>
            ) : null}
        </div>
    );

    // ── Sélection d'attendus (mode note) ──
    const blocAttendus = !decompose && (
        <div>
            <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                Attendus évalués
            </label>
            <p className="text-[12px] font-semibold text-slate-400 mb-2">
                Cochez sur chaque attendu ses compétences travaillées : la note sera reportée sur chaque case.
            </p>
            <SelecteurAttendusCompact
                themes={themes}
                attendus={attendusNiveau}
                selectionnes={attendusSelectionnes}
                onToggle={onToggleAttendu}
                competences={competences}
                onToggleComp={onToggleCompAttendu}
                niveauClasse={niveauClasse}
            />
        </div>
    );

    return (
        <div className="space-y-6">
            {zoneCollage}

            {/* Tant que rien n'est collé, l'écran n'offre QUE le collage :
                la configuration n'apparaît qu'une fois les élèves détectés. */}
            {collageOk && (
                <>
                    {/* Titre, note et date sur une ligne ; les colonnes (ou les
                        attendus) sur toute la largeur : les compétences d'une
                        ligne tiennent sur une ligne. */}
                    {blocConfiguration}
                    {tableMappage}
                    {blocAttendus}

                    <div className="flex justify-end">
                        <button
                            type="button"
                            disabled={verifierDisabled}
                            onClick={onVerifier}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-extrabold px-6 py-2.5 rounded-xl transition-colors"
                        >
                            Vérifier les élèves →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default EtapeCollerConfigurer;
